import {
  Outlet,
  createBrowserRouter,
  type RouteObject,
  type RouterProviderProps,
} from 'react-router-dom';

import { clientRoutes } from './client-routes';
import { identityRoutes } from './identity-routes';
import { publicRoutes } from './public-routes';
import { standaloneRoutes } from './standalone-routes';
import { ProgressBar } from '../../shared/ui/navigation/ProgressBar';

/**
 * RootLayout — composition wrapper that lives inside the data router so
 * components depending on routing hooks (here: `ProgressBar` reads
 * `useLocation()`) can mount.
 *
 * Anything that does NOT depend on router context (Toast, ThemeBootstrap,
 * GlobalErrorBoundary) stays at the app shell in `App.tsx`, outside the
 * `<RouterProvider>` — keeps boundaries explicit.
 */
function RootLayout() {
  return (
    <>
      <ProgressBar />
      <Outlet />
    </>
  );
}

/**
 * App router instance. Plan 06 migration — replaced the classic
 * `<BrowserRouter>` + `<Routes>` setup with the data router so that
 * `useBlocker` (plan 01 F6) and `handle` / `useMatches` patterns become
 * available without further refactor.
 *
 * The route tree is composed from per-domain factories; each factory
 * returns `RouteObject[]` instead of JSX so we can spread them flatly
 * under a single `RootLayout` parent.
 */
const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    // `publicRoutes` va primero para que `/tandas/join/:code` gane a
    // `/tandas/:id` (que vive en clientRoutes). Si stand-alone fuera el
    // segundo concat, `:id = "join"` matchearía primero y se rompería.
    children: [...publicRoutes(), ...identityRoutes(), ...clientRoutes(), ...standaloneRoutes()],
  },
];

export const router: RouterProviderProps['router'] = createBrowserRouter(routes, {
  future: {
    v7_normalizeFormMethod: true,
    v7_fetcherPersist: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
  },
});
